/**
 * Central African Time (Zambia) — Africa/Lusaka (UTC+2).
 * Use for all "today", default dates, and date display across the app.
 */

export const ZAMBIA_TIMEZONE = "Africa/Lusaka";

const ZAMBIA_UTC_OFFSET_MS = 2 * 60 * 60 * 1000;

/** Current date in Zambia as YYYY-MM-DD (for date inputs and API ranges). */
export function getTodayInZambia(): string {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: ZAMBIA_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = formatter.formatToParts(new Date());
  const y = parts.find((p) => p.type === "year")?.value ?? "";
  const m = parts.find((p) => p.type === "month")?.value ?? "";
  const d = parts.find((p) => p.type === "day")?.value ?? "";
  return `${y}-${m}-${d}`;
}

/** Start and end of a Zambia date as UTC Date (for DB queries on created_at). */
export function getZambiaDayBounds(dateStr: string): { start: Date; end: Date } {
  const midnightUtc = new Date(dateStr + "T00:00:00.000Z").getTime();
  const start = new Date(midnightUtc - ZAMBIA_UTC_OFFSET_MS);
  const end = new Date(midnightUtc + 24 * 60 * 60 * 1000 - ZAMBIA_UTC_OFFSET_MS - 1);
  return { start, end };
}

/** Zambia calendar day as YYYY-MM-DD (for grouping by day). */
export function getZambiaDateKey(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: ZAMBIA_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = formatter.formatToParts(d);
  const y = parts.find((p) => p.type === "year")?.value ?? "";
  const m = parts.find((p) => p.type === "month")?.value ?? "";
  const day = parts.find((p) => p.type === "day")?.value ?? "";
  return `${y}-${m}-${day}`;
}

/** Format a date for display in Zambia time. */
export function formatInZambia(
  date: Date | string,
  options: Intl.DateTimeFormatOptions = { dateStyle: "short" }
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: ZAMBIA_TIMEZONE,
    ...options,
  }).format(d);
}

/** Format date and time in Zambia (e.g. for logs). */
export function formatDateTimeInZambia(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: ZAMBIA_TIMEZONE,
    dateStyle: "short",
    timeStyle: "short",
  }).format(d);
}

/**
 * Parse "YYYY-MM-DDTHH:mm" (from datetime-local) as Zambia time and return UTC ISO string for DB.
 * Ensures the stored created_at falls on the correct Zambia day when querying by getZambiaDayBounds.
 */
export function zambiaDateTimeToISO(dateTimeLocal: string): string {
  const match = dateTimeLocal.trim().match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return new Date(dateTimeLocal).toISOString();
  const [, y, m, d, h, min] = match;
  const year = parseInt(y!, 10);
  const month = parseInt(m!, 10) - 1;
  const day = parseInt(d!, 10);
  const hour = parseInt(h!, 10);
  const minute = parseInt(min!, 10);
  // Zambia = UTC+2, so UTC = Zambia - 2h
  const utcMs = Date.UTC(year, month, day, hour - 2, minute, 0, 0);
  return new Date(utcMs).toISOString();
}

/** First and last UTC moments for a calendar month in Africa/Lusaka (for created_at range). */
export function getZambiaMonthBounds(monthStr: string): { start: Date; end: Date } {
  const [y, m] = monthStr.split("-").map(Number);
  const firstDay = `${monthStr}-01`;
  const lastDate = new Date(Date.UTC(y!, m!, 0));
  const lastDay = lastDate.getUTCDate();
  const lastDayStr = `${monthStr}-${String(lastDay).padStart(2, "0")}`;
  return {
    start: getZambiaDayBounds(firstDay).start,
    end: getZambiaDayBounds(lastDayStr).end,
  };
}

/** Format YYYY-MM as "March 2026" in Zambia timezone. */
export function formatMonthLabel(monthStr: string): string {
  const [y, m] = monthStr.split("-").map(Number);
  const d = new Date(Date.UTC(y!, (m ?? 1) - 1, 1));
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: ZAMBIA_TIMEZONE,
    month: "long",
    year: "numeric",
  }).format(d);
}

/** Current month in Zambia as YYYY-MM. */
export function getCurrentMonthInZambia(): string {
  const today = getTodayInZambia();
  return today.slice(0, 7);
}

/** Current date and time in Zambia as YYYY-MM-DDTHH:mm for datetime-local inputs. */
export function getNowInZambiaForInput(): string {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: ZAMBIA_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = formatter.formatToParts(new Date());
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "00";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
}
